import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import useSeo from '../seo';
import heroMark from '../Mantle Logo White.png';

const ADVANTAGES = [
  { label: '01', title: 'Speed to Value', body: 'Skip the expensive consultants. Mantle ships with pre-built manufacturing and distribution logic for rapid deployments — not the 6–12 month "blank canvas" builds of the industry giants.' },
  { label: '02', title: 'Hands-On Setup & Integration', body: 'We believe in hands-on development to ensure your initial setup is primed for success. At Mantle, we actively help you onboard your master data and build seamless connectors directly into your SAP, SYSPRO, Acumatica, or legacy ERP environment.' },
  { label: '03', title: 'Built for the Operator', body: 'A site-centric experience designed for the Factory Manager and Project Manager, not just the FP&A team. We keep complex budgets exactly as simple as they need to be.' },
  { label: '04', title: 'AI-Assisted Budgeting', body: 'Let AI build your budget with you. Use natural language prompts like "Hey AI, take last year\'s costs and add 5%" to instantly generate and refine base scenarios without manual data entry.' },
];

const SECURITY = [
  { icon: '🔒', title: 'Bank-Level Encryption', body: 'All sensitive financial data is encrypted at rest (AES-256) and in transit (TLS 1.3).' },
  { icon: '🛡️', title: 'Access Control & Audit', body: 'Multi-Factor Authentication enforcement and immutable audit trails that track every data change.' },
  { icon: '⚡', title: 'Enterprise Reliability', body: 'Engineered for 99.9% uptime with automated enterprise backups and tested secondary server failover, ensuring your data is secure, recoverable, and always online.' },
];

const Home = () => {
  const { openAudit } = useOutletContext(); // Grabs the modal function from Layout

  useSeo('/');

  return (
    <>
      {/* --------------------------------------------------------- Hero --- */}
      <section className="mn-hero">
        <div className="mn-container">
          {/* Deliberately the first meaningful element in the document: a large
              image with descriptive alt text, high on the page, is one of the
              signals Google weighs before attaching a thumbnail to a result.
              It also carries the brand at full size, which the 30px nav mark
              never did. */}
          <img
            className="mn-hero-mark"
            src={heroMark}
            width="119"
            height="84"
            alt="Mantle — budgeting, forecasting and LE planning software that sits on top of your ERP"
          />
          <span className="mn-eyebrow">Strategic Planning & Budgeting Layer</span>
          <h1>
            The planning and budgeting layer that sits directly on top of your{' '}
            <span className="mn-hl">ERP</span>.
          </h1>
          <p>
            Your ERP tells you what you lost yesterday. Mantle tells you how to make a profit
            tomorrow. Build a precise, composable “Price × Quantity” budget — without breaking
            your single source of truth.
          </p>
          <div className="mn-hero-actions">
            <button type="button" className="mn-btn mn-btn-primary mn-btn-lg" onClick={openAudit}>Request a Discovery Audit</button>
            <Link to="/login" className="mn-btn mn-btn-ghost-light mn-btn-lg">Client Login</Link>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- Advantage --- */}
      <section className="mn-section mn-section-alt">
        <div className="mn-container">
          <div className="mn-section-head">
            <span className="mn-kicker">The Mantle Advantage</span>
            <h2>Capable as the giants. Accessible to the mid-market.</h2>
            <p>The titans can technically do what Mantle does — for huge upfront fees and a year of consultants. We come pre-built.</p>
          </div>
          <div className="mn-grid-2">
            {ADVANTAGES.map((a) => (
              <div className="mn-adv-item" key={a.label}>
                <span className="mn-adv-num">{a.label}</span>
                <h3>{a.title}</h3>
                <p>{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ Security --- */}
      <section className="mn-section mn-section-dark">
        <div className="mn-container">
          <div className="mn-section-head">
            <span className="mn-kicker">Enterprise-Grade</span>
            <h2>Security your CFO and IT team will sign off on</h2>
            <p>Financial data deserves financial-grade protection — before you ever book a call.</p>
          </div>
          <div className="mn-sec-grid">
            {SECURITY.map((s) => (
              <div className="mn-sec-item" key={s.title}>
                <div className="mn-sec-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- CTA --- */}
      <section className="mn-section mn-section-alt mn-cta-band">
        <div className="mn-container">
          <h2>See your own numbers implode into profit.</h2>
          <p>Book a Discovery Audit and we’ll map your value chain to the exact modules you need.</p>
          <div className="mn-hero-actions">
            <button type="button" className="mn-btn mn-btn-primary mn-btn-lg" onClick={openAudit}>
              Request a Discovery Audit
            </button>
            <Link to="/login" className="mn-btn mn-btn-ghost mn-btn-lg">Client Login</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;